import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { AuthFacade } from '../../services/auth-facade';

const DEFAULT_AUTH_ERROR_MESSAGE = 'Authentication failed. Please sign in again.';

type AuthErrorLike = {
  error?: string;
  message?: string;
  name?: string;
};

const AUTH_ERROR_CODE_MESSAGES: Record<string, string> = {
  access_denied: 'Your account is not authorized to access this application.',
  invalid_grant: 'Your session is no longer valid. Please sign in again.',
  invalid_request: 'Authentication request was invalid. Please try again.',
  interaction_required: 'Additional authentication is required. Please sign in again.',
  login_required: 'Your session expired. Please sign in again.',
  missing_refresh_token: 'Your session expired. Please sign in again.',
  mfa_required: 'Multi-factor authentication is required to continue.',
  unauthorized: 'Your account is not authorized to access this application.',
  unauthorized_client: 'Your account is not authorized to access this application.',
};

const AUTH_ERROR_NAME_MESSAGES: Record<string, string> = {
  MissingRefreshTokenError: 'Your session expired. Please sign in again.',
  MfaRequiredError: 'Multi-factor authentication is required to continue.',
  PopupCancelledError: 'Authentication was cancelled. Please try again.',
  PopupTimeoutError: 'Authentication timed out. Please try again.',
  TimeoutError: 'Authentication timed out. Please try again.',
};

function resolveAuthErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return DEFAULT_AUTH_ERROR_MESSAGE;

  const authError = error as AuthErrorLike;
  const code = authError.error?.trim().toLowerCase();
  if (code && AUTH_ERROR_CODE_MESSAGES[code]) return AUTH_ERROR_CODE_MESSAGES[code];

  const name = authError.name?.trim();
  if (name && AUTH_ERROR_NAME_MESSAGES[name]) return AUTH_ERROR_NAME_MESSAGES[name];

  const message = authError.message?.trim();
  if (message) return message;

  return DEFAULT_AUTH_ERROR_MESSAGE;
}

export const errorGuard: CanActivateFn = async (_route, _state) => {
  const authFacade = inject(AuthFacade);
  const error = authFacade.error();
  if (error) {
    sessionStorage.setItem('lastError', resolveAuthErrorMessage(error));
  }
  await authFacade.logout();
  return false;
};
